import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const LLAMADO_DURACION = 15;
const CARDS_TOTAL = 6;

function useClock() {
  const [hora, setHora] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setHora([now.getHours(), now.getMinutes(), now.getSeconds()]
        .map(n => String(n).padStart(2, '0')).join(':'));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []);
  return hora;
}

function useClima() {
  const [clima, setClima] = useState(null);
  useEffect(() => {
    fetch('https://api.open-meteo.com/v1/forecast?latitude=-38.9516&longitude=-68.0591&current=temperature_2m,weather_code')
      .then(r => r.json())
      .then(d => setClima({ temp: Math.round(d.current.temperature_2m), code: d.current.weather_code }))
      .catch(() => {});
  }, []);
  function icono(code) {
    if (code === 0) return '☀';
    if (code <= 3) return '⛅';
    if (code <= 67) return '🌧';
    if (code <= 77) return '❄';
    return '🌥';
  }
  return clima ? { icono: icono(clima.code), temp: clima.temp } : null;
}

function sonarDing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 1.2);
  } catch {}
}

const PIN_SESSION_KEY = 'turnero_sesion';
const PIN_DIAS_VALIDEZ = 15;

function getPinSesion() {
  try {
    const raw = localStorage.getItem(PIN_SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.pin || !s?.expira) return null;
    if (new Date(s.expira) < new Date()) { localStorage.removeItem(PIN_SESSION_KEY); return null; }
    return s.pin;
  } catch { return null; }
}

function guardarPinSesion(pin) {
  const expira = new Date();
  expira.setDate(expira.getDate() + PIN_DIAS_VALIDEZ);
  localStorage.setItem(PIN_SESSION_KEY, JSON.stringify({ pin, expira: expira.toISOString() }));
}

export default function Turnero() {
  const hora = useClock();
  const clima = useClima();

  const [pinSesion, setPinSesion] = useState(() => getPinSesion());
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [verificandoPin, setVerificandoPin] = useState(false);

  const [llamadoActual, setLlamadoActual] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const [visible, setVisible] = useState(false);
  const [animando, setAnimando] = useState(false);
  const [infoItems, setInfoItems] = useState([]);
  const [nombreClinica, setNombreClinica] = useState('Consultorio San Martín');
  const [audioActivo, setAudioActivo] = useState(false);

  const audioActivoRef = useRef(false);
  const ultimoIdRef = useRef(null);
  const inicializadoRef = useRef(false);
  const timerRef = useRef(null);

  async function verificarPin() {
    if (pinInput.length !== 6) { setPinError('El PIN debe tener 6 dígitos.'); return; }
    setVerificandoPin(true);
    setPinError('');
    try {
      await axios.post(`${API}/api/turnero/verificar-pin`, { pin: pinInput });
      guardarPinSesion(pinInput);
      setPinSesion(pinInput);
    } catch (e) {
      setPinError(e.response?.data?.error || 'PIN incorrecto.');
    } finally {
      setVerificandoPin(false);
    }
  }

  useEffect(() => {
    if (!pinSesion) return;
    axios.get(`${API}/api/configuracion`)
      .then(r => {
        if (r.data?.turnero_info_items) {
          try { setInfoItems(JSON.parse(r.data.turnero_info_items)); } catch {}
        }
        if (r.data?.nombre_clinica) setNombreClinica(r.data.nombre_clinica);
      })
      .catch(() => {});
  }, [pinSesion]);

  useEffect(() => {
    if (!pinSesion) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/turnero/ultimo`, {
          headers: { 'x-turnero-pin': pinSesion },
        });
        if (!data.length) { inicializadoRef.current = true; return; }
        setHistorial(data.slice(1));
        const ultimo = data[0];
        if (!inicializadoRef.current) {
          ultimoIdRef.current = ultimo.id;
          inicializadoRef.current = true;
          return;
        }
        if (ultimoIdRef.current !== ultimo.id) {
          ultimoIdRef.current = ultimo.id;
          setLlamadoActual(ultimo);
          setCountdown(LLAMADO_DURACION);
          setVisible(true);
          setAnimando(true);
          setTimeout(() => setAnimando(false), 600);
          if (audioActivoRef.current) sonarDing();
          clearInterval(timerRef.current);
          timerRef.current = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) { clearInterval(timerRef.current); return 0; }
              return prev - 1;
            });
          }, 1000);
        }
      } catch (e) {
        // PIN expirado o inválido — limpiar sesión y pedir PIN de nuevo
        if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem(PIN_SESSION_KEY);
          setPinSesion(null);
          setPinInput('');
          setPinError('La sesión del turnero expiró. Ingresá el PIN nuevamente.');
        }
      }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { clearInterval(iv); clearInterval(timerRef.current); };
  }, [pinSesion]);

  if (!pinSesion) {
    return (
      <div style={{
        minHeight: '100vh', background: '#07091a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{
          background: '#0a1428', border: '1px solid rgba(55,138,221,0.25)',
          borderRadius: 12, padding: '48px 56px', textAlign: 'center',
          minWidth: 340, maxWidth: 400,
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: '#b8ccde', marginBottom: 8 }}>
            Acceso al Turnero
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', marginBottom: 32, lineHeight: 1.5 }}>
            Ingresá el PIN de 6 dígitos para activar la pantalla de sala de espera.
          </div>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={pinInput}
            onChange={e => { setPinInput(e.target.value.replace(/\D/g, '')); setPinError(''); }}
            onKeyDown={e => e.key === 'Enter' && verificarPin()}
            placeholder="••••••"
            autoFocus
            style={{
              width: '100%', textAlign: 'center', fontSize: 32, letterSpacing: 12,
              background: '#071020', border: '1px solid rgba(55,138,221,0.3)',
              borderRadius: 8, color: '#e0e0e0', padding: '14px 0',
              outline: 'none', marginBottom: 16, boxSizing: 'border-box',
            }}
          />
          {pinError && (
            <div style={{ color: '#ef9a9a', fontSize: 13, marginBottom: 12 }}>{pinError}</div>
          )}
          <button
            onClick={verificarPin}
            disabled={verificandoPin || pinInput.length !== 6}
            style={{
              width: '100%', padding: '14px 0', borderRadius: 8,
              background: pinInput.length === 6 ? '#1a4a8a' : '#0f1f3a',
              color: pinInput.length === 6 ? '#e0e0e0' : 'rgba(255,255,255,0.25)',
              border: 'none', fontSize: 16, fontWeight: 600, cursor: pinInput.length === 6 ? 'pointer' : 'default',
              transition: 'background 0.2s',
            }}
          >
            {verificandoPin ? 'Verificando...' : 'Ingresar'}
          </button>
        </div>
      </div>
    );
  }

  function activarAudio() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      osc.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.001);
      ctx.close();
    } catch {}
    audioActivoRef.current = true;
    setAudioActivo(true);
  }

  function fmtHora(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  }

  const cards = historial.slice(0, CARDS_TOTAL);
  const empties = Array.from({ length: Math.max(0, CARDS_TOTAL - cards.length) });

  return (
    <div style={{
      minHeight: '100vh', background: '#07091a',
      color: '#d0dcea', fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1.4vh 2.8vw 1.2vh',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1vw' }}>
          <div style={{
            width: '3.2vw', height: '3.2vw', minWidth: 36, minHeight: 36,
            background: '#1a4a8a', borderRadius: '0.5vw',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5vw',
          }}>🏥</div>
          <div>
            <div style={{ fontSize: '1.2vw', color: '#4d7a9e', fontWeight: 500, letterSpacing: '0.03em' }}>
              {nombreClinica}
            </div>
            <div style={{ fontSize: '0.65vw', color: '#2d4a60', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: 2 }}>
              Neuquén
            </div>
          </div>
        </div>

        <div style={{
          fontSize: '4vw', fontWeight: 300, color: '#b8ccde',
          letterSpacing: '0.3em', fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {hora}
        </div>

        <div style={{ textAlign: 'right' }}>
          {clima ? (
            <>
              <div style={{ fontSize: '2.2vw', color: '#7aaed8', fontWeight: 400, letterSpacing: '0.05em' }}>
                {clima.icono} {clima.temp}°
              </div>
              <div style={{ fontSize: '0.7vw', color: '#3d6080', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: 4 }}>
                Despejado
              </div>
            </>
          ) : <div style={{ width: '5vw' }} />}
        </div>
      </div>

      {/* ── BODY ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '1.5vh 2.8vw', display: 'flex', flexDirection: 'column', gap: '1.4vh' }}>

        {/* LLAMADO ACTIVO */}
        <div style={{
          border: `2px solid ${visible ? 'rgba(76,175,80,0.45)' : 'rgba(255,255,255,0.06)'}`,
          borderRadius: '0.6vw', overflow: 'hidden',
          transition: 'border-color 0.5s ease',
          transform: animando ? 'scale(1.012)' : 'scale(1)',
          transition: 'border-color 0.5s ease, transform 0.35s ease',
          flex: '0 0 auto',
        }}>
          {visible && llamadoActual ? (
            <>
              <div style={{
                background: '#04100a', padding: '0.7vh 2vw',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.6vw',
                  fontSize: '0.75vw', fontWeight: 700, color: '#4caf50',
                  textTransform: 'uppercase', letterSpacing: '0.2em',
                }}>
                  <span style={{
                    width: '0.55vw', height: '0.55vw', background: '#4caf50',
                    borderRadius: '50%', display: 'inline-block',
                    animation: 'tvpulse 1.3s ease infinite',
                  }} />
                  Llamado activo
                </div>
                <span style={{ fontSize: '0.9vw', color: 'rgba(76,175,80,0.4)', fontVariantNumeric: 'tabular-nums' }}>
                  {countdown}s
                </span>
              </div>
              <div style={{ background: '#071811', padding: '2.5vh 2.8vw 3vh' }}>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
                  <div style={{ flex: 2.2, paddingRight: '2.5vw' }}>
                    <div style={{ fontSize: '0.65vw', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1vh' }}>
                      Paciente
                    </div>
                    <div style={{
                      fontSize: '5vw', fontWeight: 600, color: '#e8f5e9',
                      letterSpacing: '0.04em', lineHeight: 1.05,
                      wordBreak: 'break-word',
                    }}>
                      {llamadoActual.paciente_nombre.toUpperCase()}
                    </div>
                  </div>

                  <div style={{ width: 1, background: 'rgba(255,255,255,0.07)', flexShrink: 0, margin: '0 2.5vw' }} />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2.5vh' }}>
                    <div>
                      <div style={{ fontSize: '0.65vw', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.8vh' }}>
                        Consultorio
                      </div>
                      <div style={{ fontSize: '2.2vw', fontWeight: 600, color: '#66bb6a', letterSpacing: '0.06em' }}>
                        {llamadoActual.consultorio.toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.65vw', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.8vh' }}>
                        Médico
                      </div>
                      <div style={{ fontSize: '1.4vw', color: 'rgba(255,255,255,0.48)', fontWeight: 400 }}>
                        {llamadoActual.medico_nombre}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '5vh 0', textAlign: 'center', background: 'rgba(255,255,255,0.012)' }}>
              <div style={{ fontSize: '2.5vw', marginBottom: '1.5vh', opacity: 0.08 }}>🔔</div>
              <div style={{ fontSize: '0.8vw', color: 'rgba(255,255,255,0.12)', letterSpacing: '0.22em', textTransform: 'uppercase' }}>
                esperando llamado
              </div>
            </div>
          )}
        </div>

        {/* HISTORIAL — 6 tarjetas, 2 filas × 3 columnas */}
        <div>
          <div style={{
            fontSize: '0.6vw', fontWeight: 700, color: 'rgba(255,255,255,0.16)',
            textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: '0.8vh',
          }}>
            Historial de llamados
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.7vw' }}>
            {cards.map(l => (
              <div key={l.id} style={{
                background: '#0a1428', border: '1px solid rgba(55,138,221,0.16)',
                borderRadius: '0.45vw', padding: '1.2vh 1.3vw',
              }}>
                <div style={{
                  fontSize: '1.2vw', fontWeight: 500, color: '#7aaed8',
                  marginBottom: '0.5vh', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {l.paciente_nombre}
                </div>
                <div style={{ fontSize: '0.95vw', color: 'rgba(255,255,255,0.3)', marginBottom: '0.7vh' }}>
                  {l.consultorio}
                </div>
                <div style={{ fontSize: '1vw', color: 'rgba(55,138,221,0.48)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtHora(l.created_at)}
                </div>
              </div>
            ))}
            {empties.map((_, i) => (
              <div key={`e-${i}`} style={{
                background: 'rgba(10,20,40,0.3)', border: '1px solid rgba(55,138,221,0.05)',
                borderRadius: '0.45vw', minHeight: '7vh',
              }} />
            ))}
          </div>
        </div>

        {/* INFORMACIÓN INSTITUCIONAL */}
        {infoItems.length > 0 && (
          <div style={{
            background: '#0b1322', border: '1px solid rgba(80,120,170,0.2)',
            borderRadius: '0.5vw', padding: '1.3vh 2.2vw',
          }}>
            <div style={{
              fontSize: '0.6vw', fontWeight: 700, color: '#3d6a9a',
              textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: '1vh',
            }}>
              Información importante
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '0.5vh 0' }}>
              {infoItems.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.6vw',
                  fontSize: '1.1vw', color: 'rgba(180,210,240,0.52)',
                }}>
                  <span style={{ color: '#3d6a9a', fontSize: '1.5vw', lineHeight: 1 }}>•</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* OVERLAY activación de audio */}
      {!audioActivo && (
        <div onClick={activarAudio} style={{
          position: 'fixed', inset: 0, zIndex: 999,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          paddingBottom: '2.5vh', cursor: 'pointer',
        }}>
          <div style={{
            fontSize: '0.7vw', color: 'rgba(255,255,255,0.16)', letterSpacing: '0.15em',
            textTransform: 'uppercase', padding: '0.5vh 1.2vw',
            border: '1px solid rgba(255,255,255,0.07)', borderRadius: '2vw',
            background: 'rgba(0,0,0,0.3)',
          }}>
            Toque para activar audio
          </div>
        </div>
      )}

      <style>{`
        @keyframes tvpulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.25;transform:scale(0.65)} }
      `}</style>
    </div>
  );
}
